import { useState, useEffect, useRef } from "react";

export function useJobberSyncStatus(options = {}) {
  const { fastInterval = 5000, slowInterval = 30000, enabled = true } = options;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isVisibleRef = useRef(true);

  const fetchStatus = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/api/jobber_status", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch Jobber status");
      }

      const data = await response.json();
      setStatus(data);
      setError(null);
      setLoading(false);
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }

      console.error("Failed to fetch Jobber status:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const setupPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!enabled || !isVisibleRef.current) {
      return;
    }

    const interval = status?.business?.sync_status === "in_progress" ? fastInterval : slowInterval;
    intervalRef.current = setInterval(fetchStatus, interval);
  };

  useEffect(() => {
    if (enabled) {
      fetchStatus();
    }
  }, [enabled]);

  useEffect(() => {
    setupPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [status?.business?.sync_status, enabled, fastInterval, slowInterval]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;

      if (isVisibleRef.current) {
        fetchStatus();
        setupPolling();
      } else if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus
  };
}
